import React from 'react'
import _ from 'underscore'
import { Check, ChevronDown, ExternalLink, LoaderCircle, Plus, Search, Tags, Users, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import PillSelect from '../../../../pill_select'
import TutorResultsPartial from './partial'
import store from '../../../../../state/store'
import SearchTutors from '../add/show'

const COLUMN_HEADING_CLASSES = 'whitespace-nowrap text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground'

class TutorResults extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      adding: false,
      tutor_search_term: '',
      search_bio_input: props.ui.tutoring_job.tutor_search_bio || ''
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tutoring_job?.id !== this.props.tutoring_job?.id) {
      this.clearSearchBio()
    }
  }

  componentWillUnmount() {
    if (store.get().ui.tutoring_job.tutor_search_bio) {
      store.emit('ui:tutoring_job:tutor_search_bio', '')
    }
  }

  clearSearchBio = () => {
    if (store.get().ui.tutoring_job.tutor_search_bio) {
      store.emit('ui:tutoring_job:tutor_search_bio', '')
    }
    if (this.state.search_bio_input) {
      this.setState({ search_bio_input: '' })
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.tutoring_job !== this.props.tutoring_job)
      return true

    if (nextProps.tutor_results !== this.props.tutor_results)
      return true

    if (nextProps.tutor_proposals !== this.props.tutor_proposals)
      return true

    if (nextProps.ui !== this.props.ui)
      return true

    if (nextState !== this.state)
      return true

    return false
  }

  onFilter = (value) => {
    store.emit('ui:tutoring_job:tutor_results_filter', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id,
      { filter: value }
    )
  }

  onExperienceLevel = (value) => {
    store.emit('ui:tutoring_job:tutor_experience_level', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onActiveWithin = (value) => {
    store.emit('ui:tutoring_job:tutor_active_within', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onOnboardedAfterFilter = (value) => {
    store.emit('ui:tutoring_job:onboarded_after', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onSearchBioChange = (e) => {
    this.setState({ search_bio_input: e.target.value })
  }

  onSearchBioKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.commitSearchBio(this.state.search_bio_input)
    }
  }

  onSearchBioClear = () => {
    this.setState({ search_bio_input: '' })
    this.commitSearchBio('')
  }

  commitSearchBio = (value) => {
    store.emit('ui:tutoring_job:tutor_search_bio', value)
    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onSearchBioSuggestion = (value) => {
    this.setState({ search_bio_input: value })
    this.commitSearchBio(value)
  }


  onAdd = () => {
    this.setState({ adding: !this.state.adding })
  }

  onSearchTutorsChange = (value) => {
    this.setState({ tutor_search_term: value })
  }

  onAddTutor = (tutor) => {
    store.emit('tutoring_job:tutor_results:create', {
      tutor_id: tutor.id,
      tutoring_job_id: this.props.tutoring_job.id,
      applied: true
    })
  }

  renderFilterDropdown = (label, options, value, fallback, onClick) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            {label}: {_.findWhere(options, {value: value})?.name || fallback} <ChevronDown className="size-3.5"/>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {_.map(options, (option) => {
            return (
              <DropdownMenuItem
                key={option.value}
                className={cn(option.value === value && 'bg-muted')}
                onSelect={() => onClick(option.value)}
              >
                {option.name}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  renderTutorResults = () => {
    if (this.props.ui.tutoring_job.loading_tutors) {
      return (
        <div className="py-6 text-center">
          <LoaderCircle className="mx-auto size-8 animate-spin text-muted-foreground"/>
        </div>
      )
    }
    else if (this.props.tutor_results.length === 0) {
      return (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No results
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-2">
        {_.map(this.props.tutor_results, (tutor_result) => {
          const tutor_proposals = _.select(this.props.tutor_proposals, function (tutor_proposal) {
            return tutor_proposal.tutor.id === tutor_result.tutor.id
          })

          return (
            <TutorResultsPartial
              customer_commitment={this.props.tutoring_job.commitment}
              learner_subjects={this.props.tutoring_job.learner_subjects}
              key={tutor_result.tutor.id}
              tutor_result={tutor_result}
              tutoring_job={this.props.tutoring_job}
              tutor_proposals={tutor_proposals}
              ui={this.props.ui}
            />
          )
        })}
      </div>
    )
  }

  render() {
    const tutor_results_filter = this.props.ui.tutoring_job.tutor_results_filter
    const tutor_experience_level = this.props.ui.tutoring_job.tutor_experience_level
    const tutor_active_within = this.props.ui.tutoring_job.tutor_active_within
    const onboarded_after = this.props.ui.tutoring_job.onboarded_after

    const experienceLevelOptions = [
      { value: 'any', name: 'Any' },
      { value: '0', name: '0 Hours' },
      { value: '1_24', name: '1 - 24 Hours' },
      { value: '25_49', name: '25 - 49 Hours' },
      { value: '50_plus', name: '50 Hours +' }
    ]

    const activeWithinOptions = [
      { value: 'any', name: 'Any' },
      { value: '1_month', name: 'Last Month' },
      { value: '3_months', name: 'Last 3 Months' },
      { value: '6_months', name: 'Last 6 Months' },
      { value: '12_months', name: 'Last 12 Months' }
    ]

    const onboardedOptions = [
      { value: 'all', name: 'All Time' },
      { value: '3_months', name: 'Last 3 Months' },
      { value: '6_months', name: 'Last 6 Months' },
      { value: '1_year', name: 'Last Year' }
    ]

    const searchBioSuggestions = [
      { label: 'IEB', query: 'IEB' },
      { label: 'CAPS', query: 'CAPS' },
      { label: 'Cambridge', query: 'Cambridge' },
      { label: 'NSC', query: 'NSC' },
      { label: 'UCT', query: 'UCT | University of Cape Town' },
      { label: 'Stellenbosch University', query: 'Stellenbosch University' },
      { label: 'University of Pretoria', query: 'University of Pretoria' },
    ]

    const filterOptions = [
      { value: 'default', name: 'Contacted' },
      { value: 'searched', name: 'Potential Matches' }
    ]

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="size-4"/>
            Tutors
            <Tabs value={tutor_results_filter} onValueChange={this.onFilter} className="ml-2">
              <TabsList className="group-data-horizontal/tabs:h-8">
                {_.map(filterOptions, (option) => (
                  <TabsTrigger key={option.value} value={option.value}>
                    {option.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardTitle>
          <CardAction className="ml-auto self-center">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={this.onAdd}><Plus className="size-3.5"/> Add Tutor</Button>
              <Button asChild variant="outline" size="sm">
                <a href={this.props.tutoring_job.links.tutor_browser} target="_blank">
                  Browse <ExternalLink className="size-3.5"/>
                </a>
              </Button>
            </div>
          </CardAction>
          {tutor_results_filter === 'searched' && (
            <div className="col-span-full mt-2 flex items-center justify-between gap-2">
              <div className="flex w-5/12 items-center gap-2">
                <InputGroup className="flex-1">
                  <InputGroupAddon><Search className="size-3.5"/></InputGroupAddon>
                  <InputGroupInput
                    type="text"
                    placeholder="Search bio &amp; subjects (press Enter)"
                    value={this.state.search_bio_input}
                    onChange={this.onSearchBioChange}
                    onKeyPress={this.onSearchBioKeyPress}
                  />
                  {this.state.search_bio_input && (
                    <InputGroupAddon>
                      <button type="button" className="cursor-pointer" onClick={this.onSearchBioClear} aria-label="Clear search">
                        <X className="size-3.5"/>
                      </button>
                    </InputGroupAddon>
                  )}
                </InputGroup>
                <PillSelect
                  icon={Tags}
                  options={_.map(searchBioSuggestions, (s) => ({ value: s.query, name: s.label }))}
                  placeholder="Suggestions"
                  onChange={this.onSearchBioSuggestion}
                />
              </div>
              <div className="flex items-center gap-2">
                {this.renderFilterDropdown('Experience', experienceLevelOptions, tutor_experience_level, 'Any', this.onExperienceLevel)}
                {this.renderFilterDropdown('Activity', activeWithinOptions, tutor_active_within, 'Any', this.onActiveWithin)}
                {this.renderFilterDropdown('Onboarded', onboardedOptions, onboarded_after, 'All Time', this.onOnboardedAfterFilter)}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="min-h-[600px]">
          {this.state.adding ?
            <div className="mb-4 flex items-start gap-4 rounded-[10px] border border-border bg-muted/30 px-4 py-3">
              <p className="pt-1.5 text-sm font-semibold">Tutor</p>
              <div className="flex-1">
                <SearchTutors onChange={this.onSearchTutorsChange} onSelect={this.onAddTutor} value={this.state.tutor_search_term} />
              </div>
              <Button size="sm" onClick={this.onAddTutor}>Add</Button>
            </div>
            : null
          }
          <div className="mb-2 flex items-center px-4">
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[21%] text-left')}>
              Tutor
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'flex w-[35%]')}>
              <span className="w-1/4">State</span>
              <span className="w-3/4">Subjects</span>
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[17%]')}>
              Availability
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[8%]')}>
              Medium
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[17%]')}>
              Risks
            </div>
          </div>
          {this.renderTutorResults()}
        </CardContent>
      </Card>
    )
  }
}

export default TutorResults
